import { browser } from '$app/env'
import type { TypedSupabaseClient } from '$lib/supabase'
import type {
  OwnedAccessoryWithAccessory,
  PageDataSession,
  UserAccessory,
} from '$lib/types'
import { produce } from 'immer'
import { get, writable } from 'svelte/store'
import { emptyReloadable, type Reloadable } from './reloadable'

export type NewAccessoriesStore = Reloadable<UserAccessory[]> & {
  markAsSeen: (accessoryId: string | undefined) => Promise<void>
}

const noop = async () => {}

export const createNewAccessoriesStore = (dependencies: {
  supabase: TypedSupabaseClient
  session: PageDataSession | null
}): NewAccessoriesStore => {
  if (!browser) {
    // This store is only used to highlight new accessories to the user, so it
    // only makes sense to use it in the browser.
    return {
      ...emptyReloadable,
      markAsSeen: noop,
    }
  }

  const _newAccessories = writable<UserAccessory[]>([], () => {
    // Load the new accessories on first listener
    reload()
  })

  const reload = async (): Promise<UserAccessory[]> => {
    const $session = dependencies.session
    if (!$session) {
      _newAccessories.set([])
      return []
    }

    const { error, data } = await dependencies.supabase
      .from('owned_accessories')
      .select(
        '*, accessory:accessories!owned_accessories_accessory_id_fkey (*)',
      )
      .eq('user_uid', $session.user.id)
      .is('seen_at', null)
      .order('created_at', { ascending: true })

    if (error) {
      console.error(error.message)
      return []
    }

    const ownedAccessories = data as unknown as OwnedAccessoryWithAccessory[]

    const loadedAccessories = ownedAccessories.map<UserAccessory>(
      ({ seen_at, accessory }) => ({
        ...accessory,
        seen: !!seen_at,
      }),
    )

    _newAccessories.update((prevAccessories) => {
      // We overwrite the accessories with the newly fetched ones, but make sure
      // that we keep any previously seen accessories.
      const prevAccessoriesNotInNew = prevAccessories.filter(
        (acc) => !loadedAccessories.find((a) => a.id === acc.id),
      )
      return produce(loadedAccessories, (draft) => [
        ...prevAccessoriesNotInNew,
        ...draft,
      ])
    })
    return get(_newAccessories)
  }

  return {
    subscribe: _newAccessories.subscribe,
    reload,
    markAsSeen: async (accessoryId: string | undefined) => {
      if (!dependencies.session || !accessoryId) return

      // Immediately update the accessory from the list, so that the UI updates.
      _newAccessories.update((accessories) =>
        produce(accessories, (draft) => {
          const accessory = draft.find((a) => a.id === accessoryId)
          if (accessory) accessory.seen = true
        }),
      )

      await dependencies.supabase.rpc('mark_accessory_seen', {
        _id: accessoryId,
      })
    },
  }
}
